import { useDebounce, useWindowSize } from '@uidotdev/usehooks';
import { useMemo } from 'react';
import { ListImage } from '../../graphql/schema';
import { SelectedImage } from './ImagePreview';

const breakPoints = { 350: 1, 600: 2, 900: 3, 1100: 4, 1600: 5 };

interface Props {
  images: ListImage[];
  setSelectedImage: (image: SelectedImage) => void;
}

const indexOfSmallest = (a: number[]) => {
  var lowest = 0;
  for (var i = 1; i < a.length; i++) {
    if (a[i] < a[lowest]) lowest = i;
  }
  return lowest;
};

const MasonryGrid = ({ images, setSelectedImage }: Props) => {
  const { width: windowWidth } = useWindowSize();
  const debouncedWindowWidth = useDebounce(windowWidth, 300);

  const numberOfColumns = useMemo(() => {
    if (!debouncedWindowWidth) return 4;

    if (debouncedWindowWidth >= 1600) return 5;
    if (debouncedWindowWidth >= 1100) return 4;
    if (debouncedWindowWidth >= 900) return 3;
    if (debouncedWindowWidth >= 600) return 2;
    if (debouncedWindowWidth >= 350) return 1;

    return 4;
  }, [debouncedWindowWidth]);

  const columns = useMemo(() => {
    const cols: ListImage[][] = [];
    const heights: number[] = [];

    for (let colIndex = 0; colIndex < numberOfColumns; colIndex++) {
      cols[colIndex] = [];
      heights[colIndex] = 0;
    }

    for (const image of images) {
      const index = indexOfSmallest(heights);

      const height = image.height * (400 / image.width);

      cols[index].push(image);
      heights[index] += height;
    }

    return cols;
  }, [images, numberOfColumns]);

  return (
    <div className="flex flex-row content-stretch justify-center box-border w-full gap-2.5">
      {columns.map((column, index) => (
        <div
          key={index}
          className="flex flex-col content-stretch justify-start flex-grow flex-shrink basis-0 w-0 gap-2.5"
        >
          {column.map((image) => (
            <div key={image.id} className="flex justify-stretch">
              <img
                alt=""
                className="w-full rounded-xl shadow cursor-pointer"
                src={image.thumbnailUrl ?? ''}
                onClick={() =>
                  setSelectedImage({
                    ...image,
                    index,
                  })
                }
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export default MasonryGrid;
